import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Stethoscope } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import SearchBar from '../components/common/SearchBar';
import ListingCard from '../components/common/ListingCard';
import ListingMap from '../components/common/ListingMap';
import { specialtiesAPI } from '../lib/api';

const SpecialtyPage = () => {
  const { slug } = useParams();
  const [specialty, setSpecialty] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSpecialty();
  }, [slug]);

  const loadSpecialty = async () => {
    setLoading(true);
    try {
      const res = await specialtiesAPI.getBySlug(slug);
      setSpecialty(res.data);
      setListings(res.data.listings || []);
    } catch (error) {
      console.error('Error loading specialty:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="animate-pulse space-y-6">
            <div className="h-12 bg-gray-200 rounded w-1/3" />
            <div className="h-6 bg-gray-200 rounded w-2/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!specialty) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Specialty Not Found</h1>
          <Link to="/" className="text-teal-700 hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="specialty-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-12 md:py-16 border-b">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
              <Stethoscope className="w-7 h-7 text-blue-700" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
                {specialty.name}
              </h1>
              <p className="text-gray-600">{listings.length} providers available</p>
            </div>
          </div>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl">
            {specialty.description || `Find ${specialty.name} specialists and facilities near you.`}
          </p>
          <SearchBar />
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
          {/* Map */}
          <div className="mb-8">
            <ListingMap listings={listings} className="h-[400px]" />
          </div>

          {/* Listings */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map((listing) => (
              <ListingCard key={listing.listing_id} listing={listing} />
            ))}
          </div>

          {listings.length === 0 && (
            <div className="text-center py-12">
              <Stethoscope className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="font-heading font-semibold text-lg text-gray-900 mb-2">No providers found</h3>
              <p className="text-gray-600">Try searching for a different specialty</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default SpecialtyPage;
